import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportBrFilterOptions, reportBrIndicators, reportBrSegmentationOptions } from "../report-br";
import { ReportBrFilterSettingsForm } from "../settings-form/ReportBrFilterSettingsForm";
import { ReportBrFilterSettings } from "../settings/ReportBrFilterSettings";
import { ReportBrTableDataRequest, CertificateTypeRequest } from "../data-request/ReportBrTableDataRequest";
import { ReportBrRow } from "../table-data/ReportBrRow";
import { ReportBrJournalRequest } from "../journal/ReportBrJournalRequest";
import { CertificateType, MutuallyRedeemingLevel } from "../../../common/types/roots/fields";
import { Option } from "../../../common/types/Option";

const MONTH_FIELDS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

// Разворачивает ключи индикаторов в реальные поля строки — зеркалит buildIndicatorGroupDefs из report-br.vue.
// sumrg_group/plan_month/months_group доступны только при planByMonths=true.
function getExcelCheckFields(activeIndicatorKeys: string[], planByMonths: boolean): string[] {
    const INDICATOR_TO_FIELDS: Record<string, string[]> = {
        utv_plan: ['utv_plan'],
        utoch_plan: ['utoch_plan'],
        skor_plan: ['skor_plan'],
        svod_plan: ['plat', 'obaz'],
        registrob: ['registrob'],
        sumrg_group: planByMonths ? ['sumrg', 'curr_sumrg'] : ['sumrg'],
        ...(planByMonths ? { months_group: MONTH_FIELDS, plan_month: ['plan_month'] } : {}),
    }
    const fields = new Set<string>()
    for (const key of activeIndicatorKeys) {
        const mapped = INDICATOR_TO_FIELDS[key]
        if (mapped) mapped.forEach(f => fields.add(f))
    }
    return [...fields]
}

function filterZeroProjectsForExcel(tableData: ReportBrRow[], checkableFields: string[]): ReportBrRow[] {
    if (!checkableFields.length) return tableData
    const n = tableData.length

    const subtreeHasValue = tableData.map(row =>
        checkableFields.some(f => {
            const val = (row as unknown as Record<string, unknown>)[f]
            return !!val && Number(val) !== 0
        })
    )

    for (let i = n - 1; i >= 0; i--) {
        if (subtreeHasValue[i]) {
            const childLevel = tableData[i].level
            for (let j = i - 1; j >= 0; j--) {
                if (tableData[j].level < childLevel) {
                    subtreeHasValue[j] = true
                    break
                }
            }
        }
    }

    return tableData.filter((row, i) => {
        const t = row.type
        if (t === 'total' || t === 'total_funding') return true
        return subtreeHasValue[i]
    })
}

export class ReportBrFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportBrFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'BR') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'BR') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }
        
        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        const segmentationOptions = settings.segmentations
        if (!segmentationOptions) {
            throw new Error('error_not_found_segmentation_option')
        }
        
        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

     
        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )


        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({...option})
        })

        const statusOptions = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        let developType = options.developTypes.find(it => it.value === settings.developType)
        if (!developType) {
            throw new Error('error_not_found_develop_type')
        }
        developType = { ...developType }

        // 2. Формирование
        const form: ReportBrFilterSettingsForm = {
            // Base:
            key: 'BR',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            date: new Date(settings.date),
            segmentations: cloneDeep(segmentationOptions),
            measureUnit: measureUnitOption,
            region: regionOption,
            budgetTypes: budgetTypeOptions,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            statuses: statusOptions,
            developType: developType,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            planByMonths: settings.planByMonths ?? false,
            hideZeroProjects: settings.hideZeroProjects ?? false,
            expenses: cloneDeep(settings.expenses),
            indicators: cloneDeep(settings.indicators ?? reportBrIndicators),
            suppliers: (settings.rnnSuppliers ?? []).map(rnn => ({ value: rnn, label: rnn }))
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'BR') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (!form.segmentations) {
            throw new Error('error_input_segmentation')
        }

        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (form.developType == null) {
            throw new Error('error_input_develop_type ')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }
        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: ReportBrFilterSettings = {
            // Base:
            reportName: 'BR',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            date: strDate,
            segmentations: form.segmentations,
            budgetTypes: form.budgetTypes.map(it => it.value),
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels?.map(it => it.value) ?? [],
            statuses: form.statuses.map(it => it.value),
            region: form.region.value,
            expenses: cloneDeep(form.expenses),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            planByMonths: form.planByMonths ?? false,
            hideZeroProjects: form.hideZeroProjects ?? false,
            developType: form.developType.value ?? null,
            indicators: cloneDeep(form.indicators ?? reportBrIndicators),
            rnnSuppliers: form.suppliers?.map(it => it.value) ?? [],
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportBrFilterOptions)

        const lastDayPrevMonthMoment = moment().subtract(1, 'months').endOf('month')
        const lastDayPrevMonthDate = lastDayPrevMonthMoment.toDate()

        const classificationType = options.classificationTypes.find(it => it.value === 'EXPENSES')!

        const emptyForm: ReportBrFilterSettingsForm = {
            key: 'BR',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'BR')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: classificationType,
            dataSource: options.dataSources.find(it => it.value === 'LOADER')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields
            date: lastDayPrevMonthDate,
            region: null,
            segmentations: reportBrSegmentationOptions,
            budgetTypes: [],
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            mutuallyRedeemingLevels: [],
            statuses: options.statuses.filter(it => it.value === '39')!,
            expenses: cloneDeep(options.expenses),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            developType: options.developTypes.find(it => it.value === 1)!,
            roundUp: false,
            roundUpTo: null,
            planByMonths: false,
            hideZeroProjects: true,
            indicators: cloneDeep(reportBrIndicators),
            suppliers: [],
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'BR') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (!form.segmentations?.filter(it => it.active).length) {
            throw new Error('error_input_segmentation')
        }
        if (form.segmentations?.filter(it => it.value === 'PROJECT' && it.active).length === 0) {
            throw new Error('error_select_project_segmentation')
        }

        const kbkSegmentationActive = form.segmentations?.some(it => it.value === 'KBK' && it.active)
        const kbkKeys = ['gr', 'pgr', 'abp', 'prg', 'ppr', 'spf']
        const anyKbkExpenseActive = form.expenses?.some(e => kbkKeys.includes(e.key) && e.active)
        if (kbkSegmentationActive && !anyKbkExpenseActive) {
            throw new Error('error_select_kbk_expense_or_disable_kbk_segmentation')
        }
            

        const cutness: string[] | undefined = form.segmentations?.filter(v => v.active).map(v => v.value)
        if (cutness == null) {
            throw new Error('error_unsupported_segmentation_type')
        }

        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (form.developType == null) {
            throw new Error('error_input_develop_type ')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const classficationType = form.classificationType.value
        if (classficationType !== 'EXPENSES') {
            throw new Error('error_unsupported_class_type')
        }

        const activeExpenses = form.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        const statuses = form.statuses.map(it => Number(it.value))
        if (statuses.length <= 0) {
            throw new Error('error_input_statuses')
        }

        const params: ReportBrTableDataRequest = {
            report_date: strDate,
            region: form.region.value,
            cutness: cutness,
            budget_types: form.budgetTypes.map(it => it.value),
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: form.mutuallyRedeemingLevels.map(it => it.value),
            statuses: statuses,
            expenses: activeExpenses,
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            plan_by_months: form.planByMonths ?? false,
            hide_zero_projects: form.hideZeroProjects ?? false,
            develop_type: form.developType.value,
            rnn_suppliers: form.suppliers?.map(it => it.value) ?? [],
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/br',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }

        const responseTableData: { items: ReportBrRow[] } = await response.json()
        const tableData: ReportBrRow[] = responseTableData.items

        const scale = (form.roundUp && roundUpTo != null) ? roundUpTo : 5
        const roundVal = (val: number) => Math.round(val * Math.pow(10, scale)) / Math.pow(10, scale)

        tableData?.forEach(r => {
            r.visible = true
            r.is_expanded = true
            Object.assign(r, {
                plan_remainder_period: roundVal((r.plat ?? 0) - (r.sumrg ?? 0)),
                plan_remainder_month: roundVal((r.plan_month ?? 0) - (r.curr_sumrg ?? 0)),
            })
        })

        // 2. Формирование параметров для выгрузки журнал
        const activeIndicatorKeys = (form.indicators ?? reportBrIndicators).filter(it => it.active).map(it => it.key)
        // Для Excel применяем тот же фильтр нулевых проектов, что и на экране
        const excelItems = (form.hideZeroProjects ?? false)
            ? filterZeroProjectsForExcel(tableData, getExcelCheckFields(activeIndicatorKeys, form.planByMonths ?? false))
            : tableData

        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportBrJournalRequest = {
            reportName: "BR",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            cutness: cutness,
            report_date: strDate,
            plan_by_months: form.planByMonths ?? false,
            items: excelItems,
            indicators_order: activeIndicatorKeys,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
